import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, CircularProgress, makeStyles, Box } from '@material-ui/core';
import clsx from 'clsx';
import { NumericMetricInfo, RCNumericMetricType } from '@moderepo/mode-apis';
import { capitalizeFirst, getRobotDefinitionByRobotClass, ITEMS_PER_PAGE_5, GenericMessageBox, useQueryParams } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchIssues, fetchRobotById, fetchRobotCatalog, rcDataContext, selectIssues, selectRobotById, selectRobotCatalog, UIFetchIssueFilters }
    from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCRobotStatusContainer, RCIssuesContainer } from '.';
import { RCRobotMetricsDashboardContainer } from './RCRobotMetricsDashboardContainer';
import { RCRobotsMapContainer } from './RCRobotsMapContainer';


const useStyle = makeStyles(() => {
    return {
        robotsMap: {
            minHeight: 500,
        },
    };
});


/**
 * Define the list of fields we want to hide in the IssuesTable. This data is static so don't need define inside the component.
 */
const issuesTableDisplayHiddenFields = {
    homeId         : true,
    deviceId       : true,
    alertId        : true,
    values         : true,
    ticketLink     : true,
    createdAgentId : true,
    resolvedAgentId: true,
    resolvedAt     : true,
};



export const RCRobotDashboardPage: React.FC = () => {
    // Get the params from the route or url
    const params = useParams();
    const queryParams = useQueryParams();
    const projectId = Number(params.projectId);
    const userId = params.userId ? Number(params.userId) : undefined;
    const deviceId = params.deviceId ? Number(params.deviceId) : undefined;

    // homeId can come from route params or query params
    const homeId = params.homeId || queryParams.get('homeId') ? Number(params.homeId || queryParams.get('homeId')) : undefined;
    const { robotId } = params;

    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, projectId);
    const robot = selectRobotById(rcDataState, homeId, robotId);

    // Create an Array of robots with just 1 robot we have because the RobotMapContainer needs an Array of robots
    const robots = useMemo(() => {
        if (robot) {
            return [robot];
        }
        return undefined;
    }, [robot]);

    // Select 1 unresolved issues. We need this so that we can hide the Issues table if there isn't any issue. We don't need to fetch many, just 1.
    const issuesFilter = useMemo(() => {
        return {
            robotId,
            pageNumber: 0,
            pageSize  : 1,
            resolved  : false,
        };
    }, [robotId]);


    const unresolvedIssues = selectIssues(rcDataState, projectId, issuesFilter);

    const robotCatalog = selectRobotCatalog(rcDataState, projectId);


    // Similar to issuesTableDisplayHiddenFields but this one is dynamic so we need to define it inside component when we have data ready
    const [issuesTableDisplayFilters] = useState<UIFetchIssueFilters>({ pageNumber: 0, pageSize: 0, robotId, resolved: false });
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();


    /**
     * This useEffect is used for loading Robot data on component init
     */
    useEffect(() => {
        if (robotId && homeId && !robot) {
            rcDataDispatch(fetchRobotById(projectId, homeId, robotId)).catch(() => { /* ignore error */ });
        }
    }, [projectId, robot, robotId, homeId, rcDataDispatch]);


    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (project && !robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robotCatalog, project, projectId, rcDataDispatch]);


    useEffect(() => {
        rcDataDispatch(fetchIssues(
            projectId, issuesFilter,
        )).catch(() => {
            // ignore error because dataDispatch already handled the error
        });
    }, [issuesFilter, projectId, rcDataDispatch]);



    const hasGPS = useMemo(() => {
        // Check if the device' class support GPS. A device class support GPS if it has a sensor type LATITUDE or LONGITUDE
        if (robot && robotCatalog) {
            const robotDef = getRobotDefinitionByRobotClass(robotCatalog, robot.robotClass);
            if (robotDef) {
                return robotDef.numericMetrics.find((numericMetric: NumericMetricInfo) => {
                    return numericMetric.type === RCNumericMetricType.LATITUDE || numericMetric.type === RCNumericMetricType.LONGITUDE;
                }) !== undefined;
            }
            return false;
        }
        return false;
    }, [robotCatalog, robot]);


    return (
        <div className={clsx('page-main-content')}>
            <div className="title-bar">
                <div className="title-bar-item title-text">
                    <div className="page-title">
                        {trans('page.title.robot_overview')}
                    </div>
                    <div className="page-subtitle">
                        {trans('page.subtitle.robot_overview')}
                    </div>
                </div>
            </div>


            {project && robot && (
                <div>
                    {robot.deviceId ? (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <RCRobotStatusContainer
                                    projectId={projectId}
                                    userId={userId}
                                    homeId={robot.homeId}
                                    robotId={robot.id}
                                    robotClass={robot.robotClass}
                                />
                            </Grid>

                            {/* Only show IssuesContainer if there are unresolved issues */}
                            {unresolvedIssues && unresolvedIssues.items.length > 0 && (
                                <Grid item xs={12}>
                                    <RCIssuesContainer
                                        className={clsx('match-height')}
                                        projectId={projectId}
                                        userId={userId}
                                        homeId={robot.homeId}
                                        deviceId={robot.deviceId}
                                        itemsPerPage={ITEMS_PER_PAGE_5}
                                        readonly
                                        hiddenFields={issuesTableDisplayHiddenFields}
                                        filters={issuesTableDisplayFilters}
                                        showFilterOptions
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <RCRobotMetricsDashboardContainer
                                    projectId={projectId}
                                    homeId={robot.homeId}
                                    robotId={robot.id}
                                />
                            </Grid>

                            {hasGPS && (
                                <Grid item xs={12}>
                                    <RCRobotsMapContainer
                                        className={classes.robotsMap}
                                        projectId={projectId}
                                        robots={robots}
                                        userId={userId}
                                        deviceId={deviceId}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    ) : (
                        <Box marginTop={3}>
                            <GenericMessageBox
                                title={capitalizeFirst(trans('error.robot_is_not_connected.message'))}
                            />
                        </Box>
                    )}
                </div>
            )}

            {(!project || !robot) && (
                <CircularProgress />
            )}
        </div>
    );
};
